import styles from './QuotaMarkEditor.module.scss'
import useTranslation from '../Hooks/useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faCheck, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faTag } from '@fortawesome/pro-regular-svg-icons'
import Button from './Button'
import DropdownField from './DropdownField'
import usePrefStore from '../stores/prefs'
import { Fragment, useEffect, useMemo, useState } from 'react'
import useHelpers from '../Hooks/useHelpers'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import useRetailQuota from '../Hooks/useRetailQuota'
import type { KTMClientType } from '../types'

const MARK_PREFIX = 'QUOTAS'

interface QuotaMarkEditorProps {
   value: string | null
   onCancel: () => void
   onValidate: (mark: string) => void
   isKOT?: boolean   // true => format QUOTAS_{YYYYMM} (sans type client). Sinon KTM : QUOTAS_{TYPE}_{YYYYMM}
   top?: string
   minWidth?: string
   maxWidth?: string
   existingMarks?: string[]
   currentOrderId?: string | null
   /** Si fourni + doublon détecté : affiche un bouton pour ouvrir l'order existant (KOT/KTM) */
   onViewExisting?: (mark: string) => void
}

function QuotaMarkEditor(props: QuotaMarkEditorProps) {
   const { trans } = useTranslation()
   const { inlineStyleFromProps } = useHelpers()
   const { globalClosePopups, currentLang: curLang } = usePrefStore()
   const { KTM_CLIENT_TYPES } = useRetailQuota()
   const [selectedClientType, setSelectedClientType] = useState<KTMClientType>('RET')
   const [selectedDate, setSelectedDate] = useState<Date>(new Date())
   const [addStyle, setAddStyle] = useState<any>({})
   const [isClientTypeDropdownOpen, setIsClientTypeDropdownOpen] = useState(false)

   const isKOT = !!props.isKOT

   useEffect(() => {
      const style = inlineStyleFromProps({ top: props.top, minWidth: props.minWidth, maxWidth: props.maxWidth })
      setAddStyle(style)

      // Parser la valeur existante :
      // - KOT  : QUOTAS_202606
      // - KTM  : QUOTAS_RET_202606
      if (props.value) {
         const parts = props.value.split('_')
         if (isKOT && parts.length === 2) {
            const suffix = parts[1]
            if (suffix.length === 6) {
               const year = parseInt(suffix.substring(0, 4))
               const month = parseInt(suffix.substring(4, 6)) - 1  // 0-indexed
               if (!isNaN(year) && !isNaN(month)) {
                  setSelectedDate(new Date(year, month))
               }
            }
         } else if (!isKOT && parts.length === 3) {
            const type = parts[1] as KTMClientType
            if (type === 'RET' || type === 'ERE' || type === 'INT') {
               setSelectedClientType(type)
            }
            const suffix = parts[2]
            if (suffix.length === 6) {
               const year = parseInt(suffix.substring(0, 4))
               const month = parseInt(suffix.substring(4, 6)) - 1  // 0-indexed
               if (!isNaN(year) && !isNaN(month)) {
                  setSelectedDate(new Date(year, month))
               }
            }
         }
      }
   }, [props.top, props.minWidth, props.maxWidth, props.value, inlineStyleFromProps, isKOT])

   const currentMark = useMemo(() => {
      if (isKOT) {
         return `${MARK_PREFIX}_${moment(selectedDate).format('YYYYMM')}`
      }
      return `${MARK_PREFIX}_${selectedClientType}_${moment(selectedDate).format('YYYYMM')}`
   }, [selectedClientType, selectedDate, isKOT])

   const isDuplicate = useMemo(() => {
      if (!props.existingMarks) return false
      return props.existingMarks.includes(currentMark)
   }, [props.existingMarks, currentMark])

   function doNothing(evt) {
      evt.stopPropagation()
   }

   function onClickBackground() {
      if (props.onCancel && globalClosePopups !== 'button') {
         props.onCancel()
      }
   }

   function doCancel() {
      props.onCancel()
   }

   function doOK() {
      if (!isDuplicate) {
         props.onValidate(currentMark)
      }
   }

   function handleMonthChange(date: Date) {
      setSelectedDate(date)
   }

   const title = isKOT
      ? trans('?Quota Retail')
      : `${trans('?Modèle Quotas')} ${KTM_CLIENT_TYPES[selectedClientType]}`
   const duplicateMsg = isKOT
      ? trans('?Ce quota existe déjà')
      : trans('?Ce modèle quota existe déjà')

   return (
      <div className={styles.background} onClick={onClickBackground}>
         <div className={styles.quota_mark_editor} onClick={doNothing} style={addStyle}>
            <div className={styles.top}>
               <FontAwesomeIcon icon={faTag} />
               <span>{title}</span>
               <button onClick={doCancel}>
                  <FontAwesomeIcon icon={faXmark} />
               </button>
            </div>
            <div className={styles.detail}>
               <div className={styles.mark_row}>
                  <span className={styles.prefix}>{MARK_PREFIX}</span>
                  <span className={styles.separator}>_</span>
                  {!isKOT && (
                     <>
                        <div className={styles.client_type_wrapper}>
                           <span className={styles.hint}>(type client)</span>
                           <DropdownField
                              selectedValue={selectedClientType}
                              height={36}
                              minWidth={80}
                              isOpen={isClientTypeDropdownOpen}
                              onToggle={() => setIsClientTypeDropdownOpen(o => !o)}
                              onSelect={(id: KTMClientType) => {
                                 setSelectedClientType(id)
                                 setIsClientTypeDropdownOpen(false)
                              }}
                           >
                              {(Object.keys(KTM_CLIENT_TYPES) as KTMClientType[]).map((type) => (
                                 <Fragment key={type}>{type}</Fragment>
                              ))}
                           </DropdownField>
                        </div>
                        <span className={styles.separator}>_</span>
                     </>
                  )}
                  <div className={styles.picker_wrapper}>
                     <span className={styles.hint}>(mois de début)</span>
                     <DatePicker
                        selected={selectedDate}
                        onChange={handleMonthChange}
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        locale={curLang === 'fr' ? 'fr' : 'en-US'}
                        className={styles.month_input}
                        renderCustomHeader={({ date, decreaseYear, increaseYear }) => (
                           <div className={styles.picker_header}>
                              <button onClick={decreaseYear} type="button">
                                 <FontAwesomeIcon icon={faChevronLeft} />
                              </button>
                              <span>{date.getFullYear()}</span>
                              <button onClick={increaseYear} type="button">
                                 <FontAwesomeIcon icon={faChevronRight} />
                              </button>
                           </div>
                        )}
                     />
                  </div>
               </div>
               <div className={styles.preview}>
                  {currentMark}
               </div>
               {isDuplicate && (
                  <div className={styles.duplicate_warning}>
                     <span>{duplicateMsg}</span>
                     {props.onViewExisting && (
                        <Button small secondary onClick={() => props.onViewExisting!(currentMark)}>
                           {trans('?Voir')}
                        </Button>
                     )}
                  </div>
               )}
            </div>
            <div className={styles.footer}>
               <Button tertiary lefticon={<FontAwesomeIcon icon={faXmark} />} onClick={doCancel}>
                  {trans('?Annuler')}
               </Button>
               <Button disabled={isDuplicate} lefticon={<FontAwesomeIcon icon={faCheck} />} onClick={doOK}>
                  {trans('?Valider')}
               </Button>
            </div>
         </div>
      </div>
   )
}

export default QuotaMarkEditor
